import { ArrowForward, Celebration, Dashboard, Group, Person, Settings } from '@mui/icons-material';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { ELinks } from '@sharedInterfaces/globalEnums';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import type { ITemplateComponentProps } from '../TemplateEditor/types';
import ETTBaseTemplateComponent from './ETTBaseTemplateComponent';
import { useEmployeeTalk } from './ETTEmployeeTalkProvider';

const ETTCompletionComponent: React.FC<ITemplateComponentProps> = (props) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const permissions = useSelector((state: AppState) => state.permissions);
    const { getSelectedSkills, getSelectedCertificates, getSelectedRoles, getSelectedSuperPowers } = useEmployeeTalk();

    const selectedSkills = getSelectedSkills();
    const selectedCertificates = getSelectedCertificates();
    const selectedRoles = getSelectedRoles();
    const selectedSuperPowers = getSelectedSuperPowers();

    const NEXT_STEPS = [
        {
            icon: <Person />,
            title: 'Profil vervollständigen',
            description: 'Füge weitere Details zu deinem Profil hinzu',
            action: 'Zum Profil',
            link: '/profile/edit',
        },
        {
            icon: <Group />,
            title: 'Team kennenlernen',
            description: 'Entdecke deine Kollegen und ihre Fähigkeiten',
            action: 'Team ansehen',
            link: '/people-org/employee',
        },
        {
            icon: <Dashboard />,
            title: 'Dashboard erkunden',
            description: 'Nutze alle Features der Plattform',
            action: 'Zum Dashboard',
            link: '/home',
        },
        {
            icon: <Settings />,
            title: 'Einstellungen anpassen',
            description: 'Personalisiere deine Erfahrung',
            action: 'Einstellungen',
            link: '/settings',
            adminOnly: true,
        },
    ].filter((step) => !step.adminOnly || permissions.admin);

    const handleQuickAction = (link: string) => {
        navigate(link);
    };

    const renderViewContent = () => (
        <Box sx={{ maxWidth: 800, mx: 'auto', textAlign: 'center' }}>
            {/* Success Animation */}
            <Box sx={{ mb: 4 }}>
                <Avatar
                    sx={{
                        width: 120,
                        height: 120,
                        mx: 'auto',
                        mb: 3,
                        background: `linear-gradient(45deg, ${theme.palette.success.main} 30%, ${theme.palette.primary.main} 90%)`,
                        animation: 'bounce 2s ease-in-out infinite',
                        '@keyframes bounce': {
                            '0%, 20%, 50%, 80%, 100%': { transform: 'translateY(0)' },
                            '40%': { transform: 'translateY(-10px)' },
                            '60%': { transform: 'translateY(-5px)' },
                        },
                    }}
                >
                    <Celebration sx={{ fontSize: 60 }} />
                </Avatar>

                <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: theme.palette.success.main }}>
                    Willkommen im Team!
                </Typography>

                <Typography variant="h5" color="text.secondary" sx={{ mb: 2 }}>
                    Dein Onboarding ist abgeschlossen
                </Typography>

                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
                    Dein Profil wurde erfolgreich erstellt und dein Team wurde benachrichtigt.
                </Typography>
            </Box>
            {/* Robot Image */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                <img src="/T4lo_Robot.png" alt="T4lo Robot" style={{ width: isMobile ? 120 : 150, height: 'auto' }} />
            </Box>
            {/* Summary */}
            <Card
                elevation={0}
                sx={{
                    mb: 4,
                    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                    background: alpha(theme.palette.success.main, 0.05),
                }}
            >
                <CardContent sx={{ p: 3 }}>
                    <Typography
                        variant={isMobile ? 'subtitle1' : 'h6'}
                        sx={{ fontWeight: 600, mb: 3, color: theme.palette.success.main }}
                    >
                        Dein Onboarding-Ergebnis
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                                {selectedSkills.length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Skills
                            </Typography>
                        </Box>

                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.warning.main }}>
                                {selectedSuperPowers.length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                SuperPowers
                            </Typography>
                        </Box>

                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.secondary.main }}>
                                {selectedCertificates.length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Zertifikate
                            </Typography>
                        </Box>

                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.info.main }}>
                                {selectedRoles.length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Rollen
                            </Typography>
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* Notification Status */}
            <Card
                elevation={0}
                sx={{
                    mb: 4,
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                    background: alpha(theme.palette.info.main, 0.05),
                }}
            >
                <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
                        <Typography
                            variant={isMobile ? 'subtitle1' : 'h6'}
                            sx={{ fontWeight: 600, color: theme.palette.info.main }}
                        >
                            Dein Team wurde benachrichtigt
                        </Typography>
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                        Dein Teamleiter wurde über dein abgeschlossenes Onboarding informiert und wird sich bald bei dir
                        melden.
                    </Typography>
                </CardContent>
            </Card>

            {/* Next Steps */}
            <Card
                elevation={0}
                sx={{
                    mb: 4,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    background: alpha(theme.palette.background.paper, 0.7),
                }}
            >
                <CardContent sx={{ p: 3 }}>
                    <Typography
                        variant={isMobile ? 'subtitle1' : 'h6'}
                        sx={{ fontWeight: 600, mb: 3, textAlign: 'left' }}
                    >
                        Empfohlene nächste Schritte
                    </Typography>

                    <List sx={{ p: 0 }}>
                        {NEXT_STEPS.map((step) => (
                            <ListItem
                                key={step.title}
                                sx={{
                                    px: 0,
                                    py: 1.5,
                                    borderRadius: 2,
                                    alignItems: 'flex-start',
                                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) },
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: isMobile ? 40 : 56, mt: 0.5 }}>
                                    <Avatar
                                        sx={{
                                            width: isMobile ? 32 : 40,
                                            height: isMobile ? 32 : 40,
                                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                                            color: theme.palette.primary.main,
                                        }}
                                    >
                                        {React.cloneElement(step.icon, { sx: { fontSize: isMobile ? 16 : 24 } })}
                                    </Avatar>
                                </ListItemIcon>
                                <Box sx={{ flex: 1 }}>
                                    <ListItemText
                                        primary={step.title}
                                        secondary={step.description}
                                        primaryTypographyProps={{ fontWeight: 600 }}
                                    />
                                    {isMobile && (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            endIcon={<ArrowForward />}
                                            onClick={() => handleQuickAction(step.link)}
                                            sx={{
                                                mt: 1,
                                                px: 1,
                                                fontSize: '0.75rem',
                                                '&:hover': { bgcolor: theme.palette.primary.main, color: 'white' },
                                            }}
                                        >
                                            {step.action}
                                        </Button>
                                    )}
                                </Box>
                                {!isMobile && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        endIcon={<ArrowForward />}
                                        onClick={() => handleQuickAction(step.link)}
                                        sx={{
                                            ml: 2,
                                            px: 2,
                                            '&:hover': { bgcolor: theme.palette.primary.main, color: 'white' },
                                        }}
                                    >
                                        {step.action}
                                    </Button>
                                )}
                            </ListItem>
                        ))}
                    </List>
                </CardContent>
            </Card>

            {/* Final Action */}
            <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Button
                    variant="contained"
                    size="medium"
                    onClick={() => {
                        navigate(`/${ELinks.DASHBOARD_EMPLOYEE}`);
                    }}
                    startIcon={<Dashboard />}
                    sx={{
                        px: isMobile ? 2 : 4,
                        py: isMobile ? 1 : 1.5,
                        fontSize: isMobile ? '0.875rem' : '1.1rem',
                        fontWeight: 600,
                        background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`,
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.4)}`,
                        },
                        transition: 'all 0.3s ease',
                    }}
                >
                    Zum Dashboard
                </Button>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Viel Erfolg in deiner neuen Rolle!
                </Typography>
            </Box>
        </Box>
    );

    return <ETTBaseTemplateComponent {...props} renderViewContent={renderViewContent} />;
};

export default ETTCompletionComponent;
